'use client'
import { useContext, useEffect, useState } from 'react'

import { AnalyticsErrorBoundary } from '@/components/client/analytics/error-boundary'
import type { CurrentUser } from '@/lib/supabase/auth/get-current-user'
import { AbSegment, PageType } from '@/types/nav-types'

import { AffiliateLinkProvider } from './affiliate-link-provider'
import { AnalyticsProvider } from './analytics-provider'
import { ClientContext } from './client-context'
interface ClientProviderProps extends React.PropsWithChildren {
  segment: AbSegment
  pageType: PageType
  // Active font for this render ('inter' | 'system'), computed server-side from
  // FONT_STRATEGY + segment (see SegmentShell). Stamped on analytics events so
  // the FONT_STRATEGY=ab test can compare conversion by font. Defaults to 'inter'.
  fontVariant?: 'inter' | 'system'
  // No longer accepted from the server — see useServerContext. Auth state is
  // hydrated client-side via /api/me so the server render stays cacheable.
}

export const ClientProvider: React.FC<ClientProviderProps> = ({
  children,
  segment,
  pageType,
  fontVariant = 'inter',
}) => {
  // undefined = /api/me hasn't resolved yet; null = signed out; user = signed in.
  // Components that render auth-state UI check for undefined to skip a flicker
  // between "Sign In" and "Sign Out brian@…" on the first paint.
  const [user, setUser] = useState<CurrentUser | null | undefined>(undefined)

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    fetch('/api/me', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : { user: null }))
      .then((data) => {
        if (!cancelled) {
          setUser(data.user ?? null)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <ClientContext.Provider value={{ pageType, user }}>
      <AnalyticsProvider segment={segment} pageType={pageType} fontVariant={fontVariant}>
        <AnalyticsErrorBoundary>
          <AffiliateLinkProvider>{children}</AffiliateLinkProvider>
        </AnalyticsErrorBoundary>
      </AnalyticsProvider>
    </ClientContext.Provider>
  )
}

export const useClientContext = () => useContext(ClientContext)
